import { InputNumber, InputNumberProps } from 'antd';
import Decimal from 'decimal.js-light';
import { useCallback, useMemo } from 'react';
import { useInputController } from '../hooks/useInputController';
import { useFormItemAccessibility } from '../item/useFormItemAccessibility';
import { useFormItemEnabled } from '../item/useFormItemEnabled';

export interface CurrencyInputProps
  extends Omit<InputNumberProps, 'name' | 'type'> {
  name: string;
  currency?: string;
  // Numero di cifre decimali fisse da mostrare
  decimalPlaces?: number;
  hideCurrency?: boolean;
  /** Funzione che trasforma il valore in input */
  transformValue?:
    | ((
        parsedValue: Decimal | string | number | null,
      ) => number | string | null)
    | null;
}

/**
 * Campo per input di tipo Valuta.
 */
export function CurrencyInput(props: CurrencyInputProps) {
  const {
    name,
    currency,
    decimalPlaces,
    hideCurrency,
    transformValue,
    ...inputProps
  } = props;

  const hasFixedDecimalPlaces = typeof decimalPlaces === 'number';
  const decimalRegex = useMemo(() => {
    if (!hasFixedDecimalPlaces) return new RegExp('');
    // Regex che viene usato nel `formatted`
    // per rimuovere le cifre decimali dopo la n-esima dove n = decimalPlaces
    // return new RegExp(`\\.(\\d{${decimalPlaces}})(\\d+)`, 'g');
    return new RegExp(`^\\d*\\.?\\d{0,${decimalPlaces}}`);
  }, [decimalPlaces, hasFixedDecimalPlaces]);

  const { enabled, layoutEnabled } = useFormItemEnabled();

  const {
    field: { value, onChange, onBlur, ref },
  } = useInputController(name, { enabled });

  const accessibility = useFormItemAccessibility();

  // Forzo il salvataggio del dato il dato come numero
  const handleChange = useCallback(
    (value: any) => {
      let parsedValue =
        value === '' || value == null ? null : new Decimal(value);
      if (parsedValue !== null && hasFixedDecimalPlaces) {
        parsedValue = parsedValue.toDecimalPlaces(
          decimalPlaces,
          Decimal.ROUND_DOWN,
        );
      }
      onChange(transformValue?.(parsedValue) ?? parsedValue);
    },
    [decimalPlaces, hasFixedDecimalPlaces, onChange, transformValue],
  );

  const onKeyDown = useCallback((e: React.KeyboardEvent<HTMLInputElement>) => {
    // Permettiamo l'uso di tasti speciali
    if (e.metaKey || e.ctrlKey) return;
    // Bloccare input di caratteri non numerici o separatori decimali
    if (e.key.match(/^[^0-9,.]$/)) {
      e.preventDefault();
    }
  }, []);

  const formatter = useCallback(
    (
      changedValue: any,
      info: {
        userTyping: boolean;
        input: string;
      },
    ) => {
      // Se l'utente sta scrivendo, uso il valore dell'input
      const inputVal = info.userTyping ? info.input : `${changedValue}`;
      if (!hasFixedDecimalPlaces) return inputVal.replace('.', ',');
      // Rimuovo le cifre decimali in eccesso
      const match = `${inputVal}`.replace(',', '.').match(decimalRegex);
      let formatted = match?.[0] ?? '';
      if (!info.userTyping && formatted) {
        // Se l'utente non sta scrivendo (onBlur), fisso il formato a 2 decimali
        formatted = new Decimal(formatted).toFixed(2);
      }
      return formatted.replace('.', ',');
    },
    [hasFixedDecimalPlaces, decimalRegex],
  );

  const parser = useCallback(
    (value: string | undefined) => {
      let parsed = value
        ? value.replace(/[^\d,.]+/g, '').replace(',', '.')
        : '';
      if (hasFixedDecimalPlaces && parsed.match(/\.\d{2}0+/)) {
        // Nel caso ci sono zeri dopo la seconda cifra decimale
        // concateno un 1 per forzare l'evento 'change'
        parsed += '1';
      }
      return parsed;
    },
    [hasFixedDecimalPlaces],
  );

  return (
    <InputNumber
      size="large"
      style={{ width: '100%' }}
      min={0}
      max={9_999_999_999_999}
      {...accessibility.attributes}
      ref={ref}
      {...inputProps}
      value={value}
      onChange={handleChange}
      onBlur={onBlur}
      name={name}
      onKeyDown={onKeyDown}
      // Viene chiamato ogni volta che il valore di 'value' cambia
      formatter={formatter}
      pattern="[\d,]+"
      decimalSeparator=","
      parser={parser}
      prefix={!hideCurrency ? currency ?? '€' : undefined}
      suffix={inputProps.suffix ?? <span />} // See https://ant.design/components/input#why-input-lose-focus-when-change-prefixsuffixshowcount
      disabled={enabled === false || layoutEnabled === false}
    />
  );
}
